import React, { forwardRef, useMemo } from 'react'
import {
  IEcommerceAnswerSchema,
  IEcommerceQuestionSchema,
  EcommerceResponse,
  EcommerceBannerPlacement
} from '@aytm/app/swidget/base/questions/Ecommerce/types'
import { getMediumAssetPath } from '@aytm/lib/common/assets.ts'
import { formatPriceWithLocale } from '@aytm/app/swidget/base/questions/Ecommerce/utils/formatPriceWithLocale.ts'
import useSurveyLocale from '@aytm/app/swidget/base/modules/locale/useSurveyLocale.ts'
import useViewport from '@aytm/app/swidget/base/modules/viewport/useViewport.ts'
import EmptyLink from '@aytm/app/swidget/base/components/EmptyLink.tsx'
import Rating from './Rating.tsx'
import AddToCartButton from './AddToCartButton.tsx'
import Carousel from '@aytm/app/swidget/base/questions/Ecommerce/components/Carousel.tsx'
import { useBannerImpression } from '../../hooks/useBannerImpression'
import style from './Card.module.scss'
import { hiddenClass } from '@aytm/app/swidget/base/questions/Ecommerce/utils/hiddenClass.ts'

interface ICardProps {
  answer: IEcommerceAnswerSchema
  schema: IEcommerceQuestionSchema
  index: number
  response: ArrayElementType<EcommerceResponse['answers']>
  deliveryDate: string
  findability: boolean
  disabled?: boolean
  small?: boolean
  zIndex: number
  showDropdown: boolean
  applyCustomWidth?: boolean
  bannerPlacement?: EcommerceBannerPlacement
  onAddToCart: (qnt: number, index: number, dropdown: boolean) => void
  onItemSelect: (index: number, bannerPlacement?: string) => void
  onSidebarShow: (index: number) => void
}

const Card = forwardRef<HTMLDivElement, ICardProps>(
  (
    {
      answer,
      schema,
      index,
      response,
      deliveryDate,
      findability,
      disabled,
      zIndex,
      onAddToCart,
      onItemSelect,
      small,
      showDropdown,
      applyCustomWidth = false,
      onSidebarShow,
      bannerPlacement
    },
    ref
  ) => {
    const viewport = useViewport()
    const { t, languageTag } = useSurveyLocale()
    const imageCollection = useMemo(() => {
      const mainImage = answer.image ? [answer.image] : []
      const gallery = answer.gallery?.length ? answer.gallery : []
      return [...mainImage, ...gallery]
    }, [answer])
    const isDiscounted = answer.discounted_price > 0 && answer.price > answer.discounted_price

    // Track banner impression for featured products. Hook is called unconditionally
    // (React Rules of Hooks); a null `placement` short-circuits inside the hook when
    // there is no bannerPlacement. `bannerRef` is returned in both cases; we route
    // the forwarded `ref` from parent only when there is NO bannerPlacement.
    const bannerRef = useBannerImpression(answer.id, bannerPlacement ?? null)
    const combinedRef = bannerPlacement ? bannerRef : ref
    return (
      <div
        ref={combinedRef}
        className={`
        ${style.card}
        ${hiddenClass(answer)}
        ${viewport.mobile ? style.cardMobile : ''}
        ${small ? style.firstRow : ''}
        ${disabled ? style.disabled : ''}
        ${
          applyCustomWidth && null !== schema.products_per_row && !viewport.mobile
            ? style[`card${schema.products_per_row}PerRow`]
            : ''
        }
      `}
      >
        <div className={style.imageWrapper}>
          {viewport.mobile && !small ? (
            <div onClick={() => onItemSelect(index, bannerPlacement)}>
              {imageCollection.length ? (
                <Carousel
                  schema={answer}
                  store="default"
                  controls={false}
                  pagination="target"
                  smallPagination={true}
                  ignoreDefault={true}
                  logEvents={false}
                />
              ) : (
                <div className={style.noImage} />
              )}
            </div>
          ) : (
            <div onClick={() => onItemSelect(index, bannerPlacement)}>
              {answer.image && answer.image.original_url ? (
                <img
                  data-testid="SW-SH-ECommerceImage"
                  className={style.image}
                  alt={answer.image?.alt_text || t('questions', 'img', 'noAlt')}
                  src={getMediumAssetPath(answer.image)}
                />
              ) : (
                <div className={style.noImage} />
              )}
            </div>
          )}
        </div>
        <div className={style.content}>
          {answer.best_seller && (!small || !viewport.mobile) && (
            <div className={style.highlyRated}>&#9733;&nbsp;{t('questions', 'ec', 'target', 'highly')}</div>
          )}
          <div className={style.prices}>
            {isDiscounted && (
              <>
                <div className={style.discount}>
                  <span
                    className={style.nowPrice}
                    dangerouslySetInnerHTML={{
                      __html: `${formatPriceWithLocale(answer.discounted_price, schema.price_mask, languageTag, true, false)}`
                    }}
                  ></span>
                  {answer.price > 0 && (
                    <>
                      <span className={style.reg}>{t('questions', 'ec', 'target', 'was')}</span>
                      <span
                        className={`${style.lastPrice}`}
                        dangerouslySetInnerHTML={{
                          __html: `${formatPriceWithLocale(answer.price, schema.price_mask, languageTag, true, false)}`
                        }}
                      />
                    </>
                  )}
                  {answer.per_count_price && <span className={style.perCount}>{answer.per_count_price}</span>}
                </div>
                {small ? '' : <div className={style.sale}>{t('questions', 'ec', 'target', 'newLowerPrice')}</div>}
              </>
            )}
            {!isDiscounted && answer.price && (
              <>
                <span
                  className={style.defaultPrice}
                  dangerouslySetInnerHTML={{
                    __html: formatPriceWithLocale(answer.price, schema.price_mask, languageTag, true, false)
                  }}
                />
                {answer.per_count_price && <span className={style.perCount}>{answer.per_count_price}</span>}
              </>
            )}
          </div>
          <div
            className={style.description}
            dangerouslySetInnerHTML={{ __html: answer.text }}
            onClick={() => onItemSelect(index, bannerPlacement)}
          />
          {!small && (
            <div className={style.rating}>
              <Rating rating={answer.rating || 0} appearence="single" />
              <EmptyLink color="#CC0000">
                <div
                  className={style.reviews}
                  dangerouslySetInnerHTML={{
                    __html: `(${answer.reviews?.toLocaleString() || 0})`
                  }}
                />
              </EmptyLink>
            </div>
          )}
          {small ? (
            ''
          ) : (
            <div className={style.info}>
              <div
                className={style.caption}
                dangerouslySetInnerHTML={{
                  __html: answer.sub_title || ''
                }}
              />
              {answer.red_label && (
                <div className={style.newAt}>
                  <span
                    className={style.text}
                    dangerouslySetInnerHTML={{
                      __html: `${answer.sub_title ? '&nbsp;|&nbsp;' : ''} ${answer.red_label}&nbsp `
                    }}
                  ></span>
                  <span className={style.logo}>¬</span>
                </div>
              )}
            </div>
          )}
          {answer.overal_pick && !small && (
            <div className={style.limited}>{t('questions', 'ec', 'target', 'limited')}</div>
          )}
          {small ? (
            ''
          ) : (
            <div className={style.shipping}>
              <strong>{t('questions', 'ec', 'target', 'shipping')}&nbsp;</strong>
              <span
                className={style.green}
                dangerouslySetInnerHTML={{
                  __html: `${t('questions', 'ec', 'target', 'arrives')}&nbsp;${deliveryDate}`
                }}
              />
            </div>
          )}
          {small ? '' : <div className={style.free}>{t('questions', 'ec', 'target', 'shippingDates')}</div>}
          <div className={style.buttons}>
            {viewport.mobile ? (
              <AddToCartButton
                index={index}
                value={response?.v || 0}
                findability={findability}
                width={viewport.mobile ? '100%' : '85px'}
                zIndex={zIndex}
                limit={schema.max_qty_per_product}
                size="small"
                showDropdown={showDropdown}
                onAddToCart={onAddToCart}
              />
            ) : (
              <>
                {0 === response?.v ? (
                  <button className={style.addToCart} onClick={() => onSidebarShow(index)}>
                    {t('questions', 'ec', 'target', 'addToCart')}
                  </button>
                ) : (
                  <AddToCartButton
                    index={index}
                    value={response?.v || 0}
                    findability={findability}
                    width={viewport.mobile ? '100%' : '120px'}
                    size="small"
                    showDropdown={showDropdown}
                    limit={schema.max_qty_per_product}
                    onAddToCart={onAddToCart}
                  />
                )}
              </>
            )}
            {small ? (
              ''
            ) : (
              <EmptyLink color="#CC0000">
                <div className={style.heart}></div>
              </EmptyLink>
            )}
          </div>
          {(answer.amazon_choice || answer.sponsored) && !small && (
            <div className={style.sponsored}>{t('questions', 'ec', 'target', 'sponsor')}</div>
          )}
        </div>
      </div>
    )
  }
)

Card.displayName = 'Card'
export default Card
